import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  TextSubtitle,
  Spacer,
  Stack,
  TextBody,
  Avatar,
  Button,
  Icon,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

import { capitalizeWords, formatDate } from '../../utils';
import { SolutionVersion } from '../../xstate/prostudio.api-service.types';
import { useProStudioRoutes } from '../../hooks';

export interface VersionCardProps {
  solution: SolutionVersion;
}

export const VersionCard: FC<VersionCardProps> = ({ solution }) => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { workflows } = useProStudioRoutes();
  const testId = `ps-versions-page-${solution.env.toLowerCase()}-card`;
  const { version, env, time, first_name, last_name, picture } = solution;
  const fullName = `${first_name} ${last_name}`;

  if (!solution.version && solution.env === 'draft') {
    return (
      <Card size="xlarge" border="default" shadow="none" test-id={testId}>
        <TextSubtitle>
          <TranslationComponent i18nKey="pro-studio/common:versions.draft">
            Draft
          </TranslationComponent>{' '}
          <span color="primary">v-</span>
        </TextSubtitle>
        <Spacer size="small" />
        <TextBody size="small" variant="secondary">
          <TranslationComponent i18nKey="pro-studio/versions:cards.empty">
            To create a new draft, make an edit in the Workflows tab or load an
            older version.
          </TranslationComponent>
        </TextBody>
      </Card>
    );
  }

  return (
    <Card
      test-id={testId}
      size="xlarge"
      border="primary"
      shadow="medium"
      variant="glass"
    >
      {env === 'draft' ? (
        <>
          <Stack
            gutter="small"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack gutter="2xsmall" alignItems="baseline">
              <TextSubtitle>
                <TranslationComponent i18nKey="pro-studio/common:versions.draft">
                  Draft
                </TranslationComponent>{' '}
                <span color="primary">{version}</span>
              </TextSubtitle>
              {/* <TextBody size="small" variant="tertiary">
              (
              <TranslationComponent i18nKey="pro-studio/common:versionPrefix">
                from
              </TranslationComponent>{' '}
              v5)
            </TextBody> */}
            </Stack>
            <Button
              test-id="ps-versions-page-edit-workflows-btn"
              variant="secondary"
              size="small"
              disabled={!version}
              onClick={() => {
                navigate(workflows.path);
              }}
            >
              <Icon name="pencil" slot="prefix" size="xsmall" />
              <TranslationComponent i18nKey="pro-studio/versions:cards.button">
                Edit Workflows
              </TranslationComponent>
            </Button>
          </Stack>
        </>
      ) : (
        <TextSubtitle>
          <TranslationComponent
            i18nKey={`pro-studio/common:versions.${env as 'draft' | 'sandbox' | 'live'}`}
          >
            {env}
          </TranslationComponent>{' '}
          <span color="primary">{version || 'v-'}</span>
        </TextSubtitle>
      )}

      {fullName && time && version ? (
        <>
          <Spacer size="small" />
          <Stack gutter="2xsmall">
            <TextBody size="small">
              {time ? formatDate(time) : ''}{' '}
              <TranslationComponent i18nKey="pro-studio/common:userNamePrefix">
                by
              </TranslationComponent>{' '}
            </TextBody>
            <Stack gutter="2xsmall" nowrap>
              <Avatar size="small" label={fullName} src={picture} />
              <TextBody size="small">{fullName}</TextBody>
            </Stack>
          </Stack>
        </>
      ) : env !== 'draft' ? (
        <>
          <Spacer size="small" />
          <Stack gutter="2xsmall">
            <TextBody size="small">
              {t('pro-studio/versions:cards.no_published_label', {
                env: capitalizeWords(env),
              })}
            </TextBody>
          </Stack>
        </>
      ) : null}
    </Card>
  );
};
